import type { Span } from './types';
import { taskDetailSpans } from './procedureOwnership';
import { TaskRunOutcomes } from './TaskRunOutcomes';
import { Dialog, DialogContent, DialogTitle } from 'src/components/ui/dialog';
import { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { useGT } from 'gt-react';
import { DesktopIcon, ArrowsOutIcon } from '@phosphor-icons/react';
import { AGENT_RUN_TIMELINE_POLL_MS } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { Button } from 'src/components/ui/button';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from 'src/components/ui/resizable';
import { StatPill, LivePill } from './atoms';
import { TraceSkeleton } from './TraceSkeleton';
import { SpanTable } from './SpanTable';
import { SpanInspector } from './SpanDetailPanel';
import { useRunSpans } from './useRunSpans';
import { useInspectionState } from './useInspectionState';
const RunRecording = lazy(() =>
  import('./RunRecording').then((module) => ({ default: module.RunRecording })),
);
import { ActionNavigation } from './ActionNavigation';
import { runClock, type RunClock } from './spanClock';
import { useTraceState } from './useTraceState';
import { countSpansByType, countTotalSpans, fmtMs, fmtTokens } from './utils';
import {
  hasKernelTelemetry,
  notableKernelTelemetryCount,
} from 'src/components/common/kernelTelemetryDisplay';

// --- AgentTraceTable ---

interface RunInspectorProps {
  defaultView?: 'actions' | 'trace';
  /** When set, the host owns which projection is showing. */
  view?: 'actions' | 'trace';
  /** Hide the Actions / Full Trace switcher when the host already tabs them. */
  hideViewToggle?: boolean;
  initialOperatorStepId?: string;
  embedded?: boolean;
  contextSlot?: React.ReactNode;
  agentRunId: string;
  agentName?: string;
  /** When true, hides the stat strip. Use for inline/compact contexts. */
  compact?: boolean;
  /** Host page already renders the recorded task results. */
  hideTaskOutcomes?: boolean;
  /** Max height of the body area. Defaults to 70vh for full, 65vh for compact. */
  maxHeight?: string;
  /** Optional session viewer content rendered as a peer tab alongside the trace. */
  sessionSlot?: React.ReactNode;
  /** Reveal a captured frame in the host page's screen panel. */
  onSelectScreenshot?: (span: Span) => void;
  onInspectionChange?: (open: boolean) => void;
}

/** Inspect a run with persistent selection and a layout sized to its available container. */
export function RunInspector(props: RunInspectorProps) {
  const evidence = useRunSpans(props.agentRunId, {
    includeOperatorSteps: true,
  });
  return <RunInspectorView {...props} evidence={evidence} />;
}

/** Presentation is independent of fetching so live and recorded hosts share it. */
export function RunInspectorView(
  props: RunInspectorProps & { evidence: ReturnType<typeof useRunSpans> },
) {
  const { spans, timeline, isLoading, isFetching } = props.evidence;
  if (
    isLoading ||
    !timeline ||
    (spans.length === 0 && !hasRecording(timeline) && props.sessionSlot == null)
  )
    return (
      <TracePlaceholder
        isLoading={isLoading}
        isLive={timeline?.run.status === 'running'}
        isFetching={isFetching}
        compact={Boolean(props.compact)}
      />
    );
  return <LoadedRunInspector {...props} timeline={timeline} />;
}

function LoadedRunInspector({
  defaultView = 'trace',
  view: controlledView,
  hideViewToggle = false,
  initialOperatorStepId,
  embedded = false,
  contextSlot,
  evidence,
  timeline: data,
  compact = false,
  hideTaskOutcomes = false,
  maxHeight,
  sessionSlot,
  onSelectScreenshot,
  onInspectionChange,
}: RunInspectorProps & {
  evidence: ReturnType<typeof useRunSpans>;
  timeline: NonNullable<ReturnType<typeof useRunSpans>['timeline']>;
}) {
  // Task-loop phase spans reference child agent runs (AgentRun.toolRunId); the hook fetches each
  // child's timeline and nests it under its phase span, mirroring the workflow trace's per-step
  // agent-run inlining.
  const { spans, isLoading, isFetching, refreshScreenshots } = evidence;
  const {
    expandedView,
    setExpandedView,
    technicalTable,
    setTechnicalTable,
    actions,
    selected,
    state,
    changeView,
    shownSpans,
    containerRef,
    stacked,
    monitorOpen,
    setMonitorOpen,
    typeCounts,
    totalSpans,
    bodyMaxHeight,
    selectedView,
    showTechnical,
  } = useInspectorLayout(
    spans,
    isLoading,
    defaultView,
    initialOperatorStepId,
    maxHeight,
    compact,
    controlledView,
  );
  const isLive = data.run.status === 'running';

  const run = data.run;
  const recording = recordingContent(data, sessionSlot);
  const visibleRecording = monitorOpen ? recording : undefined;
  const duration = run.duration ?? 0;
  const inspector = (
    <div
      ref={containerRef}
      className="flex min-w-0 flex-col border border-border rounded-lg overflow-hidden bg-white"
    >
      {contextSlot}
      <TraceToolbar
        view={selectedView}
        onViewChange={changeView}
        hasActions={actions.length > 0}
        hideViewToggle={hideViewToggle}
        embedded={embedded}
        onRefresh={refreshScreenshots}
        technicalTable={technicalTable}
        setTechnicalTable={setTechnicalTable}
        expandedView={expandedView}
        setExpandedView={setExpandedView}
        hasSession={Boolean(recording)}
        sessionNotableCount={notableKernelTelemetryCount(
          data.kernelTelemetrySummary,
        )}
        monitorOpen={monitorOpen}
        setMonitorOpen={setMonitorOpen}
      />
      <TaskRunOutcomes
        hidden={hideTaskOutcomes}
        outcomes={taskDetailSpans(spans)}
        runStartedAt={run.startedAt}
      />
      <TraceRunSummary
        compact={compact}
        duration={run.duration}
        totalSpans={totalSpans}
        outputTokens={run.totalOutputTokens}
        sessionSlot={visibleRecording}
      />

      <ActionNavigation
        actions={actions}
        selected={selected}
        onSelect={state.setSelectedId}
      />
      {/* Body */}
      <TraceInspectorBody
        spans={shownSpans}
        state={state}
        stacked={stacked}
        bodyMaxHeight={bodyMaxHeight}
        technicalTable={showTechnical}
        duration={duration}
        typeCounts={typeCounts}
        isLive={isLive}
        isFetching={isFetching}
        clock={runClock(run.startedAt, spans)}
        onRetryScreenshot={refreshScreenshots}
        onInspectionChange={onInspectionChange}
        onSelectScreenshot={hostScreenshotAction(
          onSelectScreenshot,
          setExpandedView,
          state.setSelectedId,
          onInspectionChange,
        )}
      />
    </div>
  );
  return (
    <TraceView expanded={expandedView} onExpandedChange={setExpandedView}>
      {inspector}
    </TraceView>
  );
}

/** Preserve the same inspector when expanding it into a dialog. */
function TraceView({
  expanded,
  onExpandedChange,
  children,
}: {
  expanded: boolean;
  onExpandedChange: (open: boolean) => void;
  children: React.ReactNode;
}) {
  const gt = useGT();
  if (!expanded) return children;
  return (
    <Dialog open onOpenChange={onExpandedChange}>
      <DialogContent className="flex h-[95vh] w-[96vw] max-w-none flex-col p-3 sm:max-w-none">
        <DialogTitle>
          {gt('Run Inspection', { context: 'expanded trace dialog title' })}
        </DialogTitle>
        {children}
      </DialogContent>
    </Dialog>
  );
}

/** Close expanded inspection so the host's screenshot panel is visible. */
function hostScreenshotAction(
  onSelect: ((span: Span) => void) | undefined,
  setExpandedView: (open: boolean) => void,
  setSelectedId: (id: string) => void,
  onInspectionChange?: (open: boolean) => void,
) {
  if (!onSelect) return undefined;
  return (span: Span) => {
    // Legacy host players know projected IDs, and may not contain additional
    // actions from a canonical generation. Inspect those frames here instead.
    if (span.operatorEvidence && !span.projectionEvidence) {
      setSelectedId(span.id);
      onInspectionChange?.(true);
      return;
    }
    setExpandedView(false);
    onSelect(span.projectionEvidence ?? span);
  };
}

/** Keep the trace and selected evidence in resizable panes, stacking them in narrow containers. */
function TraceInspectorBody({
  spans,
  state,
  stacked,
  bodyMaxHeight,
  technicalTable,
  duration,
  typeCounts,
  isLive,
  isFetching,
  clock,
  onInspectionChange,
  onRetryScreenshot,
  onSelectScreenshot,
}: {
  spans: Span[];
  state: ReturnType<typeof useTraceState>;
  stacked: boolean;
  bodyMaxHeight: string;
  technicalTable: boolean;
  duration: number;
  typeCounts: ReturnType<typeof countSpansByType>;
  isLive: boolean;
  isFetching: boolean;
  clock: RunClock | null;
  onInspectionChange?: (open: boolean) => void;
  onRetryScreenshot?: () => Promise<unknown>;
  onSelectScreenshot?: (span: Span) => void;
}) {
  return (
    <ResizablePanelGroup
      direction={stacked ? 'vertical' : 'horizontal'}
      className="overflow-hidden"
      style={{ height: bodyMaxHeight, minHeight: '400px' }}
    >
      <ResizablePanel
        defaultSize={28}
        minSize={stacked ? 25 : 20}
        className="min-w-0"
      >
        <SpanTable
          compact={!technicalTable}
          // A row click opens its evidence in the side pane. The pane sits beside the list, so
          // selecting never buries the trace the way an in-place panel would; subtrees open on
          // the caret.
          compactRowAction="inspect"
          spans={spans}
          totalDurMs={duration}
          expanded={state.expanded}
          onToggle={state.toggleExpand}
          selected={state.selected}
          onSelect={(span) => {
            state.setSelectedId(span.id);
            onInspectionChange?.(true);
          }}
          onSelectScreenshot={onSelectScreenshot}
          query={state.query}
          onQueryChange={state.setQuery}
          filters={state.filters}
          typeCounts={typeCounts}
          onToggleType={state.toggleType}
          onToggleStatus={state.toggleStatus}
          onSetMinTokens={state.setMinTokens}
          onClearFilters={state.clearFilters}
          onExpandAll={state.expandAll}
          onCollapseAll={state.collapseAll}
          showFilters={state.showFilters}
          onToggleShowFilters={state.toggleShowFilters}
          isLive={isLive}
          isFetching={isFetching}
          pollIntervalMs={AGENT_RUN_TIMELINE_POLL_MS}
          clock={clock}
        />
      </ResizablePanel>

      {state.selected && (
        <>
          <ResizableHandle />
          <ResizablePanel
            defaultSize={72}
            minSize={stacked ? 30 : 40}
            className="min-w-0"
          >
            <SpanInspector
              key={state.selected.id}
              span={state.selected}
              onRetryScreenshot={onRetryScreenshot}
              onClose={() => {
                onInspectionChange?.(false);
                state.setSelectedId(null);
              }}
            />
          </ResizablePanel>
        </>
      )}
    </ResizablePanelGroup>
  );
}

/** Expose optional diagnostics, expanded inspection, and session recording controls. */
function TraceToolbar({
  view,
  onViewChange,
  hasActions,
  hideViewToggle,
  embedded,
  onRefresh,
  technicalTable,
  setTechnicalTable,
  expandedView,
  setExpandedView,
  hasSession,
  sessionNotableCount,
  monitorOpen,
  setMonitorOpen,
}: {
  view: 'actions' | 'trace';
  onViewChange: (view: 'actions' | 'trace') => void;
  hasActions: boolean;
  hideViewToggle: boolean;
  embedded: boolean;
  onRefresh: () => Promise<unknown>;
  technicalTable: boolean;
  setTechnicalTable: (value: boolean) => void;
  expandedView: boolean;
  setExpandedView: (value: boolean) => void;
  hasSession: boolean;
  sessionNotableCount: number;
  monitorOpen: boolean;
  setMonitorOpen: (value: boolean) => void;
}) {
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b p-2">
        <InspectorViewButtons
          hidden={hideViewToggle}
          hasActions={hasActions}
          view={view}
          onViewChange={onViewChange}
        />
        <InspectorToolbarActions
          embedded={embedded}
          onRefresh={onRefresh}
          technicalTable={technicalTable}
          setTechnicalTable={setTechnicalTable}
          expandedView={expandedView}
          setExpandedView={setExpandedView}
        />
      </div>
      <SessionRecordingToggle
        hasSession={hasSession}
        sessionNotableCount={sessionNotableCount}
        monitorOpen={monitorOpen}
        setMonitorOpen={setMonitorOpen}
      />
    </>
  );
}

function InspectorViewButtons({
  hidden,
  hasActions,
  view,
  onViewChange,
}: {
  hidden: boolean;
  hasActions: boolean;
  view: 'actions' | 'trace';
  onViewChange: (view: 'actions' | 'trace') => void;
}) {
  const gt = useGT();
  if (hidden) return <div className="flex gap-1" />;
  return (
    <div className="flex gap-1">
      {hasActions && (
        <Button
          variant="ghost"
          size="sm"
          aria-pressed={view === 'actions'}
          onClick={() => onViewChange('actions')}
        >
          {gt('Actions', { context: 'run inspector view' })}
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        aria-pressed={view === 'trace'}
        onClick={() => onViewChange('trace')}
      >
        {gt('Full Trace', { context: 'run inspector view' })}
      </Button>
    </div>
  );
}

function InspectorToolbarActions({
  embedded,
  onRefresh,
  technicalTable,
  setTechnicalTable,
  expandedView,
  setExpandedView,
}: {
  embedded: boolean;
  onRefresh: () => Promise<unknown>;
  technicalTable: boolean;
  setTechnicalTable: (value: boolean) => void;
  expandedView: boolean;
  setExpandedView: (value: boolean) => void;
}) {
  const gt = useGT();
  return (
    <div className="flex gap-1">
      <Button variant="ghost" size="sm" onClick={() => void onRefresh()}>
        {gt('Refresh', { context: 'refresh shared run evidence' })}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        aria-pressed={technicalTable}
        onClick={() => setTechnicalTable(!technicalTable)}
      >
        {gt('Technical Table', {
          context: 'toggle technical trace columns',
        })}
      </Button>
      {!embedded && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpandedView(!expandedView)}
        >
          <ArrowsOutIcon className="size-4" />
          {expandedView
            ? gt('Exit Expanded View', {
                context: 'trace inspector fullscreen control',
              })
            : gt('Expand Run', {
                context: 'trace inspector fullscreen control',
              })}
        </Button>
      )}
    </div>
  );
}

function SessionRecordingToggle({
  hasSession,
  sessionNotableCount,
  monitorOpen,
  setMonitorOpen,
}: {
  hasSession: boolean;
  sessionNotableCount: number;
  monitorOpen: boolean;
  setMonitorOpen: (value: boolean) => void;
}) {
  const gt = useGT();
  if (!hasSession) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => setMonitorOpen(!monitorOpen)}
      aria-expanded={monitorOpen}
    >
      <DesktopIcon className="size-4" />
      {sessionNotableCount > 0
        ? gt('Session Recording · {count}', {
            context: 'toggle run session viewer with notable telemetry count',
            count: String(sessionNotableCount),
          })
        : gt('Session Recording', {
            context: 'toggle run session viewer',
          })}
    </Button>
  );
}

/** Distinguish an empty finished trace from a live run still waiting for its first event. */
function TracePlaceholder({
  isLoading,
  isLive,
  isFetching,
  compact,
}: {
  isLoading: boolean;
  isLive: boolean;
  isFetching: boolean;
  compact: boolean;
}) {
  const gt = useGT();
  if (isLoading) return <TraceSkeleton compact={compact} />;
  if (!isLive)
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        {gt('No trace steps recorded.', {
          context: 'agent trace empty state when run has no spans',
        })}
      </p>
    );
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <LivePill
          intervalMs={AGENT_RUN_TIMELINE_POLL_MS}
          isFetching={isFetching}
        />
        <span className="text-sm text-muted-foreground">
          {gt('Waiting for the first trace step…', {
            context: 'agent trace live placeholder before any span exists',
          })}
        </span>
      </div>
      <TraceSkeleton compact={compact} rows={3} />
    </div>
  );
}

/** Show aggregate run metrics and the optional session recording without crowding trace rows. */
function TraceRunSummary({
  compact,
  duration,
  totalSpans,
  outputTokens,
  sessionSlot,
}: {
  compact: boolean;
  duration: number | null | undefined;
  totalSpans: number;
  outputTokens: number | null | undefined;
  sessionSlot?: React.ReactNode;
}) {
  const gt = useGT();
  return (
    <>
      {!compact && (
        <div className="flex flex-wrap border-b">
          <StatPill
            label={gt('Duration', { context: 'run trace summary metric' })}
            value={fmtMs(duration)}
          />
          <StatPill
            label={gt('Recorded Spans', {
              context: 'run trace summary metric',
            })}
            value={String(totalSpans)}
          />
          <StatPill
            label={gt('Output Tokens', { context: 'run trace summary metric' })}
            value={fmtTokens(outputTokens ?? 0)}
          />
        </div>
      )}
      {sessionSlot && (
        <div className="border-b border-border">{sessionSlot}</div>
      )}
    </>
  );
}

/** Observe the active inline or dialog container after it mounts or moves. */
function useTraceContainerWidth(
  isLoading: boolean,
  expandedView: boolean,
  spanCount: number,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [isLoading, expandedView, spanCount]);
  return { containerRef, width };
}

/** Choose an expanded viewport height while honoring inline height overrides. */
function traceHeight(
  expanded: boolean,
  maxHeight: string | undefined,
  compact: boolean,
): string {
  if (expanded) return '80vh';
  if (maxHeight) return maxHeight;
  return compact ? '65vh' : '70vh';
}

function hasRecording(
  data: NonNullable<ReturnType<typeof useRunSpans>['timeline']>,
) {
  return Boolean(
    data.inspectionRecording ||
    data.recordingDocumentId ||
    data.recordingToolCalls?.length ||
    hasKernelTelemetry(data),
  );
}

function recordingContent(
  data: NonNullable<ReturnType<typeof useRunSpans>['timeline']>,
  slot?: React.ReactNode,
) {
  if (slot != null) return slot;
  if (!hasRecording(data)) return undefined;
  return (
    <Suspense fallback={<TraceSkeleton compact />}>
      <RunRecording data={data} />
    </Suspense>
  );
}

function useInspectorLayout(
  spans: Span[],
  isLoading: boolean,
  defaultView: 'actions' | 'trace',
  initialOperatorStepId: string | undefined,
  maxHeight: string | undefined,
  compact: boolean,
  controlledView?: 'actions' | 'trace',
) {
  const [expandedView, setExpandedView] = useState(false);
  const [technicalTable, setTechnicalTable] = useState(false);
  const { containerRef, width } = useTraceContainerWidth(
    isLoading,
    expandedView,
    spans.length,
  );
  const stacked = width < 900;
  const [monitorOpen, setMonitorOpen] = useState(false);

  const typeCounts = countSpansByType(spans);
  const totalSpans = countTotalSpans(spans);

  const { actions, actionsView, selected, state, changeView, shownSpans } =
    useInspectionState(
      spans,
      defaultView,
      initialOperatorStepId,
      controlledView,
    );

  const bodyMaxHeight = traceHeight(expandedView, maxHeight, compact);
  const selectedView: 'actions' | 'trace' = actionsView ? 'actions' : 'trace';
  const showTechnical = !actionsView && technicalTable;
  return {
    expandedView,
    setExpandedView,
    technicalTable,
    setTechnicalTable,
    actions,
    selected,
    state,
    changeView,
    shownSpans,
    containerRef,
    stacked,
    monitorOpen,
    setMonitorOpen,
    typeCounts,
    totalSpans,
    bodyMaxHeight,
    selectedView,
    showTechnical,
  };
}
